import React, { useMemo, useState } from "react";
import { exportAgentToExcel, printAgentPDF } from "../utils/exportHelpers";
import { isRetentionRelevant } from "../utils/calculations";
import { normalizePlate } from "../utils/parsers";
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const AgentDetail = ({ agent, rawData, aiInsights }) => {
  const [includeAI, setIncludeAI] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Helper per normalizzazione matching
  const normalize = (val) => normalizePlate(val);

  const calculateMissingPolicies = (targetPerc) => {
    if (!agent) return 0;
    // FIX: Apply same Strict Causale Logic as CalculateKPIs
    const totalExpiries = rawData.expiries.filter(
      (e) => e.collaboratore === agent.name && isRetentionRelevant(e.causale),
    ).length;

    if (totalExpiries === 0) return 0;
    const targetCount = Math.ceil((targetPerc / 100) * totalExpiries);

    // Recalculate numerator strictly for consistency if needed, or use AgentKPIs rate
    // Using AgentKPIs rate is safer as it's the source of truth for the %
    const currentCount = Math.round(
      (agent.retentionRate / 100) * totalExpiries,
    );
    return Math.max(0, targetCount - currentCount);
  };

  const deltas = useMemo(() => {
    if (!agent) return null;

    const crossSellGap = Math.max(0, 1.3 - agent.mixNonMotor);
    const missingRetentionBase = calculateMissingPolicies(70);
    const missingRetentionSuper = calculateMissingPolicies(80);

    return {
      retentionBase: missingRetentionBase,
      retentionSuper: missingRetentionSuper,
      crossSellGap: crossSellGap.toFixed(2),
      garNewGap: Math.max(0, 2.0 - agent.avgGarMotorNew).toFixed(1),
      garRenGap: Math.max(0, 1.1 - agent.avgGarMotorRen).toFixed(1),
    };
  }, [agent, rawData]);

  const agentExpiries = useMemo(
    () =>
      agent
        ? rawData.expiries.filter(
            (e) =>
              e.collaboratore === agent.name && isRetentionRelevant(e.causale),
          )
        : [],
    [agent, rawData],
  );

  // Update Renewals List: Must EXCLUDE PRP policies to match KPI logic
  const agentRenewalsRefs = useMemo(
    () =>
      agent
        ? rawData.production
            .filter(
              (p) =>
                p.collaboratore === agent.name &&
                p.premioNetto > 0 &&
                !p.codicePolizza.startsWith("PRP"),
            )
            .map((p) => normalize(p.riferimento))
        : [],
    [agent, rawData],
  );

  // Lookup for PRP policies specifically (to identify lost cause)
  const prpRefs = useMemo(
    () =>
      new Set(
        rawData.production
          .filter((p) => p.premioNetto > 0 && p.codicePolizza.startsWith("PRP"))
          .map((p) => normalize(p.riferimento)),
      ),
    [rawData.production],
  );

  const lostPlates = useMemo(() => {
    if (!agent) return [];

    const lostMap = new Map(
      rawData.lost.map((l) => [normalize(l.targa), l.ultimaCompagnia]),
    );

    return (
      agentExpiries
        .filter(
          (exp) => !agentRenewalsRefs.includes(normalize(exp.riferimento)),
        )
        .map((exp) => {
          const normRef = normalize(exp.riferimento);
          let competitor = lostMap.get(normRef);

          // Check if it was lost due to PRP rule
          if (!competitor && prpRefs.has(normRef)) {
            competitor = "PRP (Esclusa)";
          }

          return {
            riferimento: exp.riferimento,
            scadenza: exp.scadenzaCopertura,
            concorrente: competitor || "Sconosciuto",
          };
        })
        // Ordiniamo: Perse file > Perse PRP > Sconosciute
        .sort((a, b) => {
          const score = (val) => {
            if (val.startsWith("PRP")) return 2;
            if (val !== "Sconosciuto") return 3;
            return 1;
          };
          return score(b.concorrente) - score(a.concorrente);
        })
    );
  }, [agentExpiries, agentRenewalsRefs, rawData.lost, prpRefs]);

  const handleExcelExport = async () => {
    if (!agent) return;
    setIsExporting(true);
    try {
      await exportAgentToExcel(agent, rawData, aiInsights, includeAI);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = () => {
    if (!agent) return;
    printAgentPDF(agent, rawData, aiInsights, includeAI);
  };

  if (!agent) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 500,
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          textAlign: "center",
          color: "grey.400",
          border: 1,
          borderStyle: "dashed",
          borderColor: "grey.300",
        }}
      >
        <AccountCircleIcon sx={{ fontSize: 60, mb: 2, opacity: 0.2 }} />
        <Typography variant="h6" sx={{ fontWeight: "medium" }}>
          Analisi Agente
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 300, mt: 1 }}>
          Seleziona un collaboratore per visualizzare i dettagli sulle
          provvigioni.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        animation: "fadeIn 0.5s ease-out",
      }}
    >
      {/* Profile Info & Actions */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          boxShadow: 1,
          border: 1,
          borderColor: "grey.100",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                background:
                  "linear-gradient(to bottom right, #2563eb, #4338ca)",
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.25rem",
                fontWeight: "bold",
                boxShadow: 2,
              }}
            >
              {agent.name.charAt(0)}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                  letterSpacing: "-0.02em",
                }}
              >
                {agent.name}
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Chip
                  label={
                    agent.bonusLevel === 2
                      ? "Eccellenza (2%)"
                      : agent.bonusLevel === 1
                      ? "Qualità (1%)"
                      : "Nessun Bonus"
                  }
                  size="small"
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    bgcolor:
                      agent.bonusLevel === 2
                        ? "warning.100"
                        : agent.bonusLevel === 1
                        ? "info.100"
                        : "grey.100",
                    color:
                      agent.bonusLevel === 2
                        ? "warning.700"
                        : agent.bonusLevel === 1
                        ? "info.700"
                        : "grey.500",
                    borderColor:
                      agent.bonusLevel === 2
                        ? "warning.200"
                        : agent.bonusLevel === 1
                        ? "info.200"
                        : "grey.200",
                    border: 1,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Export Controls */}
        <Box sx={{ pt: 2, borderTop: 1, borderColor: "grey.50" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAI}
                    onChange={(e) => setIncludeAI(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "0.625rem",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "text.secondary",
                    }}
                  >
                    Includi Report AI
                  </Typography>
                }
              />
            </Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handlePrint}
                startIcon={<PictureAsPdfIcon sx={{ color: "error.main" }} />}
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  borderColor: "grey.200",
                  color: "text.secondary",
                  borderRadius: 3,
                  py: 1,
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                Stampa KPI
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={isExporting}
                onClick={handleExcelExport}
                startIcon={
                  isExporting ? (
                    <CircularProgress
                      size={16}
                      sx={{ color: "success.main" }}
                    />
                  ) : (
                    <TableChartIcon sx={{ color: "success.main" }} />
                  )
                }
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  borderColor: "grey.200",
                  color: "text.secondary",
                  borderRadius: 3,
                  py: 1,
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                Export KPI
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Why am I losing money? / Requirements */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          boxShadow: 1,
          border: 1,
          borderColor: "grey.100",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 900,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
            }}
          >
            Target Mancanti
          </Typography>
          <TrackChangesIcon sx={{ color: "grey.300" }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <RequirementItem
            label="Mix No-Motor (1.3%)"
            checked={agent.reqCross}
            value={`${agent.mixNonMotor.toFixed(2)}%`}
            deltaText={
              agent.reqCross ? "Target OK" : `Manca lo ${deltas?.crossSellGap}%`
            }
          />
          <RequirementItem
            label="Garanzie Nuove (2.0)"
            checked={agent.avgGarMotorNew >= 2.0}
            value={agent.avgGarMotorNew.toFixed(1)}
            deltaText={
              agent.avgGarMotorNew >= 2.0
                ? "Target OK"
                : `Mancano ${deltas?.garNewGap}`
            }
          />
          <RequirementItem
            label="Garanzie Rinnovo (1.1)"
            checked={agent.avgGarMotorRen >= 1.1}
            value={agent.avgGarMotorRen.toFixed(1)}
            deltaText={
              agent.avgGarMotorRen >= 1.1
                ? "Target OK"
                : `Mancano ${deltas?.garRenGap}`
            }
          />
          <RequirementItem
            label="Ritenzione Base (70%)"
            checked={agent.reqRetBase}
            value={`${agent.retentionRate.toFixed(1)}%`}
            deltaText={
              agent.reqRetBase
                ? "Bonus 1% OK"
                : `Recupera ${deltas?.retentionBase} polizze`
            }
          />
          <RequirementItem
            label="Ritenzione Super (80%)"
            checked={agent.reqRetSuper}
            value={`${agent.retentionRate.toFixed(1)}%`}
            deltaText={
              agent.reqRetSuper
                ? "Bonus 2% OK"
                : `Mancano ${deltas?.retentionSuper} per il Super Bonus`
            }
          />
        </Box>
      </Paper>

      {/* Churn Intelligence */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          boxShadow: 1,
          border: 1,
          borderColor: "grey.100",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.625rem",
              fontWeight: 900,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
            }}
          >
            Clienti Persi
          </Typography>
          <Chip
            label={lostPlates.length}
            size="small"
            sx={{
              bgcolor: "error.100",
              color: "error.700",
              fontWeight: "bold",
              fontSize: "0.625rem",
            }}
          />
        </Box>
        <Box
          sx={{
            maxHeight: 288,
            overflowY: "auto",
            pr: 1,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e5e7eb",
              borderRadius: "10px",
            },
          }}
        >
          {lostPlates.length > 0 ? (
            <List
              disablePadding
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              {lostPlates.map((lost, idx) => (
                <ListItem
                  key={idx}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: 1,
                    borderColor: "grey.100",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                        fontSize: "0.75rem",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {lost.riferimento}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.625rem",
                        color: "text.disabled",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      {lost.scadenza}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: "0.625rem",
                        textTransform: "uppercase",
                        color:
                          lost.concorrente !== "Sconosciuto"
                            ? "error.main"
                            : "text.disabled",
                      }}
                    >
                      {lost.concorrente}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
                textAlign: "center",
              }}
            >
              <SentimentSatisfiedAltIcon
                sx={{
                  color: "success.light",
                  fontSize: 24,
                  mb: 1,
                  opacity: 0.3,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  color: "text.disabled",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  fontStyle: "italic",
                  letterSpacing: "-0.05em",
                }}
              >
                Nessun abbandono
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

const RequirementItem = ({ label, checked, value, deltaText }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      p: 1.5,
      borderRadius: 3,
      border: 1,
      borderColor: "transparent",
      transition: "all 0.2s",
      "&:hover": { borderColor: "grey.100" },
    }}
  >
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Box
        sx={{
          mt: 0.5,
          width: 20,
          height: 20,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: checked ? "success.100" : "error.100",
          color: checked ? "success.main" : "error.main",
        }}
      >
        {checked ? (
          <CheckIcon sx={{ fontSize: 12 }} />
        ) : (
          <CloseIcon sx={{ fontSize: 12 }} />
        )}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.625rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "-0.02em",
            color: checked ? "text.primary" : "text.disabled",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.5625rem",
            mt: 0.5,
            fontWeight: "bold",
            textTransform: checked ? "uppercase" : "none",
            fontStyle: checked ? "normal" : "italic",
            color: checked ? "success.main" : "error.light",
          }}
        >
          {deltaText}
        </Typography>
      </Box>
    </Box>
    <Chip
      label={value}
      size="small"
      sx={{
        fontSize: "0.625rem",
        fontWeight: 900,
        bgcolor: checked ? "success.50" : "error.50",
        color: checked ? "success.dark" : "error.dark",
        borderRadius: 2,
      }}
    />
  </Box>
);

export default AgentDetail;
